import React, { ReactNode, forwardRef, RefObject } from 'react';
import { Button as MuiButton, CircularProgress, SxProps } from '@mui/material';
import { OverridableStyles } from '~/utils/types.ts';
import {
  Color,
  useCommanderContext,
} from '~/theme/CommanderProvider/index.tsx';

export type BtnColor = 'inherit' | 'default' | Color;

export type BtnVariant = 'text' | 'outlined' | 'contained';

export type BtnSize = 'small' | 'medium' | 'large';

export type BtnType = 'button' | 'submit' | 'reset';

export interface ButtonProps extends OverridableStyles {
  variant?: BtnVariant;
  size?: BtnSize;
  onClick?: (e: React.MouseEvent) => void;
  onFocus?: React.FocusEventHandler<HTMLButtonElement>;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  color?: BtnColor;
  isDisabled?: boolean;
  /**
   * Use this property if button does not have text.
   * If possible, avoid using this prop if you need an icon button.
   * Instead use <IconButton />, with additional styling if needed
   * @deprecated
   */
  isIconChildrenOnly?: boolean;
  fullHeight?: boolean;
  children: ReactNode;
  disableRipple?: boolean;
  type?: BtnType;
  loading?: boolean;
  dataTestId?: string;
  ref?: RefObject<HTMLButtonElement>;
  id?: string;
}

function getProgressSize(btnSize: ButtonProps['size']): number {
  if (btnSize === 'small') {
    return 18;
  }
  if (btnSize === 'large') {
    return 26;
  }
  return 24;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant,
      size,
      color,
      isDisabled,
      isIconChildrenOnly,
      fullHeight,
      children,
      styleOverrides = {},
      loading,
      dataTestId,
      ...passthroughProps
    },
    ref,
  ) => {
    const sx: SxProps = {
      ...styleOverrides,
      paddingLeft: isIconChildrenOnly ? '0' : undefined,
      paddingRight: isIconChildrenOnly ? '0' : undefined,
      minWidth:
        styleOverrides?.minWidth ?? (isIconChildrenOnly ? '0' : undefined),
      height: fullHeight ? '100%' : styleOverrides?.height,
      textTransform: styleOverrides?.textTransform ?? 'none',
      '&:hover': styleOverrides?.hover,
    };

    const { useInstoreSecondaryButtons } = useCommanderContext();
    const isSecondaryContainedButton =
      color === 'secondary' && variant === 'contained';
    const adjustedVariant: ButtonProps['variant'] =
      isSecondaryContainedButton && useInstoreSecondaryButtons
        ? 'outlined'
        : variant;

    return (
      <MuiButton
        ref={ref}
        data-test-id={dataTestId}
        disabled={isDisabled}
        variant={adjustedVariant}
        size={size}
        color={color}
        sx={sx}
        {...passthroughProps}
      >
        {loading ? (
          <CircularProgress
            sx={{ color: 'inherit' }}
            size={getProgressSize(size)}
          />
        ) : (
          children
        )}
      </MuiButton>
    );
  },
);

Button.displayName = 'Button';

export default Button;
